import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Typography, useMediaQuery, useTheme, alpha } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { useSearchParams, useParams } from 'react-router-dom';
import CustomDialog from '@ui/components/common/Dialog';
import BDrawer from '@ui/components/common/BDrawer';
import AppAlert from '@ui/components/common/Alert';
import SearchFilter from '@ui/components/Button/SearchFilter';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { getAllVehiclesByPickupLocationIdAsync } from '@application/features/pickupLocationVehicle/pickupLocationVehicleThunk';
import { assignAttendeeToVehicleAsync } from '@application/features/attendeePickupAssignment/attendeePickupAssignmentThunk';
import { getEventVehiclesNotPaginatedByEventIdAsync, assignAttendeeToRouteVehicleAsync, getRouteVehiclesByPickupLocationAsync } from '@application/features/eventVehicle/eventVehicleThunk';
import { toast } from 'react-toastify';
// const defaultOptions: ReassignVehicleItem[] = [
//   {
//     id: 1,
//     vehicleNumber: 'Vehicle# ACS - 1B2',
//     coordinator: 'Frank de Jong - Coordinator',
//     capacity: { obtained: 25, total: 50 }
//   },
//   {
//     id: 2,
//     vehicleNumber: 'Vehicle# BA - 225',
//     coordinator: 'Bruan Smith - Coordinator',
//     capacity: { obtained: 10, total: 50 }
//   },
//   {
//     id: 3,
//     vehicleNumber: 'Vehicle# RFH - 5441',
//     coordinator: 'Claudio van Hecke - Coordinator',
//     capacity: { obtained: 3, total: 50 }
//   }
// ];
function getCapacityColor(theme, obtained, total) {
    if (obtained > total)
        return 'error';
    if (obtained === total)
        return 'success';
    return 'info';
}
const ReassignVehicleDialog = ({ open, onClose, onConfirm, title, attendeeIds, locationId: propLocationId, onSuccess, isFamilyFest }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const dispatch = useAppDispatch();
    const { vehicles: standardVehicles } = useAppSelector((state) => state.pickupLocationVehicle);
    const { vehiclesNotPaginated: eventVehicles, vehiclesByLocation } = useAppSelector((state) => state.eventVehicle);
    const [searchParams] = useSearchParams();
    const locationIdFromParams = searchParams.get('locationId') || '';
    const locationId = propLocationId || locationIdFromParams;
    const { eventId } = useParams();
    const [searchValue, setSearchValue] = useState('');
    const [selectedId, setSelectedId] = useState(null);
    // Fetch vehicles when dialog opens
    useEffect(() => {
        if (!open)
            return;
        if (isFamilyFest) {
            if (locationId) {
                dispatch(getRouteVehiclesByPickupLocationAsync(locationId));
            }
            else if (eventId) {
                dispatch(getEventVehiclesNotPaginatedByEventIdAsync({ eventId, searchString: searchValue || undefined }));
            }
        }
        else if (locationId) {
            dispatch(getAllVehiclesByPickupLocationIdAsync({ pickupLocationId: locationId, searchString: searchValue || undefined }));
        }
    }, [open, locationId, searchValue, dispatch, isFamilyFest, eventId]);
    const options = useMemo(() => {
        if (isFamilyFest) {
            const sourceItems = locationId ? vehiclesByLocation : eventVehicles;
            const query = searchValue.trim().toLowerCase();
            const filtered = query && locationId // Only local filter if from location, otherwise server-side filter used in useEffect above
                ? sourceItems.filter((v) => v.vehicleNumber.toLowerCase().includes(query))
                : sourceItems;
            return filtered
                .filter((v) => v.routeVehicleId)
                .map((v) => ({
                id: v.routeVehicleId,
                vehicleNumber: v.vehicleNumber ? `Vehicle# ${v.vehicleNumber}` : '—',
                coordinator: '—',
                capacity: { obtained: v.assignedCount || 0, total: v.totalCapacity || 0 }
            }));
        }
        if (!standardVehicles)
            return [];
        return standardVehicles.map((vehicle) => ({
            id: vehicle.id,
            vehicleNumber: vehicle.vehicleNumber ? `Vehicle# ${vehicle.vehicleNumber}` : '—',
            coordinator: vehicle.coordinatorName ? `${vehicle.coordinatorName} - Coordinator` : '—',
            capacity: { obtained: vehicle.assignedCount || 0, total: vehicle.totalCapacity || 0 }
        }));
    }, [isFamilyFest, locationId, eventVehicles, vehiclesByLocation, standardVehicles, searchValue]);
    const selected = options.find((o) => o.id === selectedId);
    const handleConfirm = async () => {
        if (!selected)
            return;
        // If attendeeIds are provided, use bulk assignment
        if (attendeeIds && attendeeIds.length > 0) {
            try {
                if (isFamilyFest) {
                    await dispatch(assignAttendeeToRouteVehicleAsync({ assignmentIds: attendeeIds, routeVehicleId: selected.id })).unwrap();
                    toast.success(t('events.eventDetails.transportTab.attendeesAssignedSuccessfully', { count: attendeeIds.length }));
                    onSuccess?.();
                }
                else {
                    const response = await dispatch(assignAttendeeToVehicleAsync({ attendeeIds, vehicleId: selected.id })).unwrap();
                    if (response.isSuccess) {
                        toast.success(response.message || t('events.eventDetails.transportTab.attendeesAssignedSuccessfully', { count: attendeeIds.length }));
                        onSuccess?.();
                    }
                    else {
                        toast.error(response.message || t('events.eventDetails.transportTab.attendeeAssignFailed'));
                    }
                }
            }
            catch (error) {
                toast.error(error?.error || t('events.eventDetails.transportTab.attendeeAssignFailed'));
            }
        }
        else {
            // Use the existing onConfirm callback for single assignment
            onConfirm?.(selected);
        }
        setSelectedId(null);
        onClose();
    };
    const handleClose = () => {
        setSearchValue('');
        setSelectedId(null);
        onClose();
    };
    const dialogActions = (_jsxs(_Fragment, { children: [_jsx(Button, { variant: 'outlined', color: 'inherit', onClick: handleClose, size: 'large', fullWidth: true, children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'inherit', size: 'large', fullWidth: true, disabled: selectedId == null, onClick: handleConfirm, endIcon: _jsx(Icon, { icon: 'lucide:check' }), children: t('actions.confirm') })] }));
    const content = (_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 2, children: [_jsx(AppAlert, { severity: 'info', children: t('events.eventDetails.transportTab.reassignVehicleAlert') }), _jsx(SearchFilter, { fullWidth: true, forceDesktopVariant: true, searchValue: searchValue, onSearchChange: setSearchValue, placeholder: t('events.eventDetails.transportTab.searchVehicles'), breakpoint: 'lg', onClear: () => setSearchValue(''), widthBox: '100%', bgVariant: 'grey8' }), _jsxs(Box, { sx: {
                    maxHeight: 'calc(100dvh - 400px)',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5
                }, children: [options.map((opt) => {
                        const isSelected = selectedId === opt.id;
                        const capColor = getCapacityColor(theme, opt.capacity.obtained, opt.capacity.total);
                        return (_jsxs(Box, { onClick: () => setSelectedId(opt.id), sx: {
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                p: 2,
                                borderRadius: 3,
                                border: '1px solid',
                                borderColor: isSelected ? 'primary.main' : alpha(theme.palette.grey[500], 0.24),
                                bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'background.paper',
                                cursor: 'pointer',
                                '&:hover': {
                                    bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.12) : 'action.hover'
                                }
                            }, children: [_jsx(Box, { display: 'flex', alignItems: 'center', gap: 1, children: _jsx(Chip, { size: 'medium', label: `${opt.capacity.obtained}/${opt.capacity.total} ${t('events.eventDetails.transportTab.seats')}`, color: capColor, variant: 'soft', sx: { fontWeight: 500 } }) }), _jsx(Typography, { variant: 'body1', children: opt.vehicleNumber }), _jsxs(Box, { display: 'flex', alignItems: 'center', gap: 0.5, children: [_jsx(Icon, { icon: 'mdi:account-outline', width: 16, height: 16, color: theme.palette.text.secondary }), _jsx(Typography, { variant: 'caption', color: 'text.secondary', children: opt.coordinator })] })] }, opt.id));
                    }), options.length === 0 && (_jsx(Typography, { variant: 'body2', color: 'text.secondary', textAlign: 'center', py: 2, children: t('dataGrid.noResults') }))] })] }));
    if (isSmallScreen) {
        return (_jsx(BDrawer, { open: open, onClose: handleClose, title: title || t('events.eventDetails.transportTab.reassignVehicle'), anchor: 'bottom', showBottomBar: true, dialogActions: dialogActions, children: _jsx(Box, { sx: { p: 2 }, children: content }) }));
    }
    return (_jsx(CustomDialog, { open: open, onClose: handleClose, titleWithIcon: title || t('events.eventDetails.transportTab.reassignVehicle'), titleIcon: '/assets/icons/vehicle-icon.png', maxWidth: 'sm', backgroundVariant: 'white', closeIcon: true, actions: dialogActions, children: _jsx(Box, { p: 2, children: content }) }));
};
export default ReassignVehicleDialog;
